import React, { useEffect } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { LoaderRow } from '../../components/atoms/layout/LoaderRow';
import { NotFoundPage } from '../../../mainv2/containers/common/NotFoundPage';
import { useNavigation } from '../../components/hocs/withNavigation';
import { NavigationSpecs } from '../../states/NavigationState';
import { SubmissionBodySection } from '../../components/organisms/lesson_homeworks/SubmissionBodySection';
import { SubmissionPreview } from '../../components/organisms/course_works/SubmissionPreview';
import { ReviewPlaceholder } from '../../components/organisms/course_works/ReviewPlaceholder';
import { SubmissionCommentList } from '../../components/organisms/submission_comments/SubmissionCommentList';
import { SubmissionCommentForm } from '../../components/organisms/submission_comments/SubmissionCommentForm';
import { useCurrentUserState } from '../../../commonv3/hooks/useCurrentUserState';
import { ReviewCompleteBadge } from '../../components/organisms/course_works/ReviewCompleteBadge';
import { ExamPracticalSectionSubmissionShowPageState } from './states/ExamPracticalSectionSubmissionShowPageState';
import { restrictAccessForUser } from '../../../commonv3/hooks/restrictAccessForUser';
import { SubmissionEvaluationResultPane } from '../../components/organisms/submission_evaluation_results/SubmissionEvaluationResult';
import { BreadCrumb } from '../breadcrumb/BreadCrumb';
import { V3Routes } from '../../routes';
import { ExamPracticalSectionHeaderContent } from '../../components/organisms/exam_sections/ExamPracticalSectionHeaderContent';
import { GrayPage } from '../../components/atoms/layout/gray_page/GrayPage';
import { GrayPageHeader } from '../../components/atoms/layout/gray_page/GrayPageHeader';
import { GrayPageBody } from '../../components/atoms/layout/gray_page/GrayPageBody';
import './examPracticalSectionSubmissionShowPage.module.scss';

type Props = {
  match: {
    params: {
      submissionToken: string;
    };
  };
};

export const ExamPracticalSubmissionShowPage = observer(
  ({
    match: {
      params: { submissionToken },
    },
  }: Props) => {
    restrictAccessForUser();
    useNavigation(NavigationSpecs.side(), NavigationSpecs.none());
    const { pageState } = useLocalStore(() => {
      return {
        pageState: new ExamPracticalSectionSubmissionShowPageState(),
      };
    });
    useEffect(() => {
      pageState.init(submissionToken);
    }, [submissionToken]);
    const { submission: submissionResource, commentState, submitComment, submissionEvaluationResultState } = pageState;
    if (submissionResource.loading && submissionResource.isEmpty()) {
      return <LoaderRow />;
    }
    if (submissionResource.isEmpty()) {
      return <NotFoundPage entityType="実技試験" />;
    }
    const submission = submissionResource.getOrThrow();
    const currentUser = useCurrentUserState();
    const submissionEvaluationResult = submissionEvaluationResultState.evaluationResult.get();
    const bottomText = (
      <span>
        添削が完了しています。
        <br />
        担当のTAより返信が来ない場合がございますので、予めご了承くださいませ。
      </span>
    );
    const breadCrumbItems = [
      { text: '課題', to: V3Routes.userCourseWorkSubmissions() },
      { text: submission.section.title },
    ];

    return (
      <GrayPage id="exam-practical-section-submission-page">
        <GrayPageHeader>
          <BreadCrumb items={breadCrumbItems} hasHome />
          <ExamPracticalSectionHeaderContent
            examPracticalSection={submission.section}
            reviewer={submission.reviewer}
            showReviewer={true}
          />
        </GrayPageHeader>
        <GrayPageBody>
          <SubmissionBodySection title="提出内容">
            <SubmissionPreview
              user={submission.user}
              submittedFields={submission.submitted_fields}
              badge={submission.review_completed_at ? <ReviewCompleteBadge /> : null}
            />
          </SubmissionBodySection>
          {submission.review_completed_at && submissionEvaluationResult && (
            <div styleName="submission-evaluation-result-wrapper">
              <SubmissionBodySection title="採点結果">
                <SubmissionEvaluationResultPane
                  submission={submission}
                  result={submissionEvaluationResult}
                  loading={submissionEvaluationResultState.evaluationResult.loading}
                />
              </SubmissionBodySection>
            </div>
          )}
          <SubmissionBodySection title="添削コメント">
            <ReviewPlaceholder submission={submission} />
            <SubmissionCommentList
              viewerId={currentUser.userId}
              loading={commentState.comments.loading}
              comments={commentState.comments.getOrElse([])}
            />
            {submission.review_completed_at && submission.user_id == currentUser.userId && (
              <SubmissionCommentForm
                user={currentUser.currentUser}
                formState={commentState.formState}
                onSubmit={submitComment}
                bottomText={bottomText}
                loading={commentState.formState.submitting}
              />
            )}
          </SubmissionBodySection>
        </GrayPageBody>
      </GrayPage>
    );
  }
);
